import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ROLES } from '../constants/roles';

interface FlaggedAccount {
  id: string;
  userId: string;
  email: string;
  reason: string;
  timestamp: any;
  autoFlagged: boolean;
  hourlyQuestions?: number;
  plan?: string;
  stripeCustomerId?: string;
  accountCount?: number;
  quizzesInLastHour?: number;
}

export default function FlaggedAccountsAdmin() {
  const { userDoc } = useAuth();
  const navigate = useNavigate();
  const [flaggedAccounts, setFlaggedAccounts] = useState<FlaggedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Check admin access (master admin only)
  useEffect(() => {
    if (userDoc && userDoc.role !== ROLES.MASTER) {
      navigate('/dashboard');
    }
  }, [userDoc, navigate]);

  useEffect(() => {
    loadFlaggedAccounts();
  }, []);

  const loadFlaggedAccounts = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'flaggedAccounts'),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
      const snapshot = await getDocs(q);
      const accounts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as FlaggedAccount));
      setFlaggedAccounts(accounts);
    } catch (error) {
      console.error('Error loading flagged accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFlag = async (flagId: string) => {
    if (!confirm('Clear this flag? The account will be unmarked.')) return;

    setActionLoading(flagId);
    try {
      await deleteDoc(doc(db, 'flaggedAccounts', flagId));
      setFlaggedAccounts(prev => prev.filter(a => a.id !== flagId));
    } catch (error) {
      console.error('Error clearing flag:', error);
      alert('Failed to clear flag');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBanAccount = async (account: FlaggedAccount) => {
    if (!confirm(`Ban account ${account.email}? This will suspend their access.`)) return;

    setActionLoading(account.id);
    try {
      // Update user document to suspend account
      const userRef = doc(db, 'users', account.userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        alert('User not found');
        return;
      }

      await updateDoc(userRef, {
        status: 'suspended',
        isActive: false,
        suspendedAt: new Date(),
        suspendReason: `Admin action: ${account.reason}`,
        plan: 'free',
        subscriptionStatus: 'canceled'
      });

      // Remove from flagged accounts
      await deleteDoc(doc(db, 'flaggedAccounts', account.id));
      setFlaggedAccounts(prev => prev.filter(a => a.id !== account.id));

      alert('Account banned successfully');
    } catch (error) {
      console.error('Error banning account:', error);
      alert('Failed to ban account');
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewUser = async (userId: string) => {
    setActionLoading(userId);
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const message = `
User Details:
━━━━━━━━━━━━━━━━━━━━━━
Email: ${userData.email}
Name: ${userData.displayName || 'N/A'}
Plan: ${userData.plan || 'N/A'}
Status: ${userData.status || 'N/A'}
Created: ${userData.createdAt ? new Date(userData.createdAt).toLocaleString() : 'N/A'}
Students: ${userData.studentsLimit || 0}
Stripe Customer: ${userData.stripeCustomerId || 'None'}
━━━━━━━━━━━━━━━━━━━━━━
        `.trim();
        alert(message);
      } else {
        alert('User not found');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      alert('Failed to fetch user details');
    } finally {
      setActionLoading(null);
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }
    return new Date(timestamp).toLocaleString();
  };

  const getRiskLevel = (account: FlaggedAccount): 'high' | 'medium' | 'low' => {
    if (account.reason.includes('100 q/hour') || account.reason.includes('bot')) return 'high';
    if (account.reason.includes('50 q/hour') || account.reason.includes('Multiple accounts')) return 'medium';
    return 'low';
  };

  const getRiskBadgeClass = (level: 'high' | 'medium' | 'low') => {
    switch (level) {
      case 'high': return 'bg-red-100 text-red-800 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading flagged accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">🚨 Flagged Accounts</h1>
              <p className="text-gray-600 mt-1">
                Review suspicious activity and abuse detection
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadFlaggedAccounts}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition duration-200"
              >
                🔄 Refresh
              </button>
              <button
                onClick={() => navigate('/master-admin')}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition duration-200"
              >
                ← Back to Admin
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">Total Flagged</div>
            <div className="text-3xl font-bold text-gray-900">{flaggedAccounts.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">High Risk</div>
            <div className="text-3xl font-bold text-red-600">
              {flaggedAccounts.filter(a => getRiskLevel(a) === 'high').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">Medium Risk</div>
            <div className="text-3xl font-bold text-yellow-600">
              {flaggedAccounts.filter(a => getRiskLevel(a) === 'medium').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">Auto-Flagged</div>
            <div className="text-3xl font-bold text-blue-600">
              {flaggedAccounts.filter(a => a.autoFlagged).length}
            </div>
          </div>
        </div>

        {/* Accounts Table */}
        <div className="bg-white rounded-lg shadow">
          {flaggedAccounts.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">✅</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Flagged Accounts</h3>
              <p className="text-gray-600">All clear! No suspicious activity detected.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Risk
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Flagged At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {flaggedAccounts.map((account) => {
                    const riskLevel = getRiskLevel(account);
                    const isLoading = actionLoading === account.id || actionLoading === account.userId;

                    return (
                      <tr key={account.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getRiskBadgeClass(riskLevel)}`}>
                            {riskLevel.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{account.email}</div>
                          <div className="text-xs text-gray-500">{account.plan || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{account.reason}</div>
                          {account.autoFlagged && (
                            <span className="text-xs text-blue-600">🤖 Auto-detected</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {account.hourlyQuestions && (
                            <div>Hourly: {account.hourlyQuestions} q</div>
                          )}
                          {account.accountCount && (
                            <div>Accounts: {account.accountCount}</div>
                          )}
                          {account.quizzesInLastHour && (
                            <div>Quizzes: {account.quizzesInLastHour}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatTimestamp(account.timestamp)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewUser(account.userId)}
                              disabled={isLoading}
                              className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleClearFlag(account.id)}
                              disabled={isLoading}
                              className="text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
                            >
                              Clear
                            </button>
                            <button
                              onClick={() => handleBanAccount(account)}
                              disabled={isLoading}
                              className="text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                            >
                              {isLoading ? 'Wait...' : 'Ban'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
